import {
  Box,
  BoxProps,
  Icon,
  Text,
  Tooltip,
  useOutsideClick,
  usePrefersReducedMotion,
} from "@chakra-ui/react";
import { ElementType, useEffect, useRef, useState } from "react";
import { FaCheck } from "react-icons/fa";
import { useIntl } from "react-intl";
import { FOCUS_RING } from "utils/chackra/controlStyles";

export type MethodIconState = "linked" | "linkable" | "unavailable";

interface MethodIconProps {
  icon: ElementType;
  label: string;
  state: MethodIconState;
  /** What is linked; shown on hover and focus of a linked method. */
  display?: string | null;
  onClick?: () => void;
}

const TILE: BoxProps = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 1,
  w: "full",
  minH: "6rem",
  p: 3,
  borderRadius: "md",
  borderWidth: "1px",
  textAlign: "center",
};

const STATUS_ID: Record<Exclude<MethodIconState, "linked">, string> = {
  linkable: "signInMethods.link",
  unavailable: "signInMethods.notAvailable",
};

// Replaces the tooltip's scale-and-fade under prefers-reduced-motion.
const NO_MOTION = {
  variants: {},
  initial: false,
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, transition: { duration: 0 } },
  transition: { duration: 0 },
} as const;

/**
 * A linked method: not a control, but what is linked must still be reachable.
 * A disabled button cannot take focus or fire hover, so the tooltip hangs off a
 * focusable wrapper; the button itself stays out of the tab order.
 *
 * Two independent reasons to be open. `hoverOrFocus` is Chakra's own and it may
 * close it whenever it likes. `pinned` is a tap or click, which Chakra never
 * touches: it ignores touch, and the native pointerleave on finger up schedules
 * a close that lands after the click, which would shut a tooltip it shared state
 * with. A tap elsewhere, Escape or blur unpins.
 */
const LinkedTile = ({
  icon,
  label,
  display,
}: Pick<MethodIconProps, "icon" | "label" | "display">) => {
  const intl = useIntl();
  const reducedMotion = usePrefersReducedMotion();
  const [hoverOrFocus, setHoverOrFocus] = useState(false);
  const [pinned, setPinned] = useState(false);
  const isOpen = hoverOrFocus || pinned;
  const wrapper = useRef<HTMLDivElement>(null);
  // A tap focuses the wrapper, which opens the tooltip before the click lands;
  // the toggle has to work from what the user saw when the press began.
  const openWhenPressed = useRef(false);

  useOutsideClick({ ref: wrapper, enabled: pinned, handler: () => setPinned(false) });

  useEffect(() => {
    if (!pinned) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPinned(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [pinned]);

  return (
    <Tooltip
      label={display}
      isDisabled={!display}
      hasArrow
      isOpen={isOpen}
      onOpen={() => setHoverOrFocus(true)}
      onClose={() => setHoverOrFocus(false)}
      closeOnClick={false}
      closeOnPointerDown={false}
      motionProps={reducedMotion ? NO_MOTION : undefined}
    >
      <Box
        ref={wrapper}
        tabIndex={0}
        role="group"
        aria-label={intl.formatMessage(
          { id: display ? "signInMethods.linkedLabel" : "signInMethods.linkedLabelBare" },
          { method: label, display }
        )}
        borderRadius="md"
        cursor="not-allowed"
        _focusVisible={FOCUS_RING}
        onPointerDown={() => {
          openWhenPressed.current = isOpen;
        }}
        onClick={() => {
          setPinned(!openWhenPressed.current);
          if (openWhenPressed.current) setHoverOrFocus(false);
        }}
        onBlur={() => setPinned(false)}
      >
        <Box
          as="button"
          type="button"
          disabled
          tabIndex={-1}
          pointerEvents="none"
          borderColor="border.subtle"
          {...TILE}
        >
          <Icon as={icon} boxSize={5} color="text.primary" aria-hidden="true" />
          <Text fontSize="sm" fontWeight="medium" color="text.primary">
            {label}
          </Text>
          <Text
            as="span"
            display="inline-flex"
            alignItems="center"
            gap={1}
            fontSize="xs"
            fontWeight="semibold"
            color="status.successStrong"
          >
            <Icon as={FaCheck} boxSize={3} aria-hidden="true" />
            {intl.formatMessage({ id: "signInMethods.linked" })}
          </Text>
        </Box>
      </Box>
    </Tooltip>
  );
};

/**
 * One sign-in method: an icon, a visible label and a visible status. Only a
 * linkable method is a live button. No state uses the 50% disabled opacity: a
 * disabled tile still carries information (AA applies), so it reads as a
 * status through its text and icon rather than by fading.
 */
const MethodIcon = ({ icon, label, state, display, onClick }: MethodIconProps) => {
  const intl = useIntl();

  if (state === "linked") {
    return <LinkedTile icon={icon} label={label} display={display} />;
  }

  const status = intl.formatMessage({ id: STATUS_ID[state] });

  if (state === "linkable") {
    return (
      <Box
        as="button"
        type="button"
        onClick={onClick}
        aria-label={intl.formatMessage(
          { id: "signInMethods.linkLabel" },
          { method: label, action: status }
        )}
        borderColor="border.strong"
        cursor="pointer"
        _hover={{ bg: "bg.surfaceHover" }}
        _focusVisible={FOCUS_RING}
        {...TILE}
      >
        <Icon as={icon} boxSize={5} color="brand.accent" aria-hidden="true" />
        <Text fontSize="sm" fontWeight="medium" color="text.primary">
          {label}
        </Text>
        <Text as="span" fontSize="xs" fontWeight="semibold" color="brand.solid">
          {status}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      as="button"
      type="button"
      disabled
      borderColor="border.subtle"
      cursor="not-allowed"
      {...TILE}
    >
      <Icon as={icon} boxSize={5} color="text.secondary" aria-hidden="true" />
      <Text fontSize="sm" fontWeight="medium" color="text.secondary">
        {label}
      </Text>
      <Text as="span" fontSize="xs" color="text.secondary">
        {status}
      </Text>
    </Box>
  );
};

export default MethodIcon;
